import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import CustomLoading from '../../../util_components/custom_loading';
import ConfirmSaveModal from './confirm_save_modal';
import EditModal from './edit_modal';
import EntriesTable from './entries_table';
import EntryForm from './entry_form';
import LookupPicker from './lookup_picker';
import RemoveModal from './remove_modal';
import UploadPanel from './upload/panel';
import {
  create_additional,
  fetch_additional_categories,
  fetch_additional_list,
  fetch_additional_sessions,
  fetch_additional_teachers,
  remove_additional,
  update_additional
} from '../api';
import {
  already_saved_view,
  categories_error_text,
  closes_modal,
  edit_failure_view,
  error_view,
  failure_route,
  for_month_text,
  is_already_saved_error,
  is_confirm_large_error,
  is_duplicate_error,
  list_error_view,
  list_header,
  list_view,
  lost_create_text,
  month_write_state,
  mutation_result,
  notify_type,
  other_month_failure_text,
  pending_list_view,
  row_view,
  save_failure_view,
  SURFACE
} from './adapters';
import {
  category_label,
  category_options,
  create_payload,
  duplicate_matches,
  needs_amount_confirm,
  previous_month_carry,
  remove_payload,
  saved_entry_conflicts,
  update_payload
} from './form_logic';
import {
  ERROR,
  LIMITS,
  LIST_STATUS,
  POLICY_FALLBACK,
  SOURCE,
  api_pay_month,
  apply_list_drafts,
  initial_list_state,
  list_state_for_month,
  policy_limits
} from './params';
import {
  forget_saved_entry,
  read_saved_guard,
  refresh_saved_entries,
  remember_saved_entry,
  write_saved_guard
} from './saved_guard';
import { fmt, month_label } from '../format';
import { ist_month_key } from '../shared/ist';
import { last_page_offset } from '../shared/paging';
import { toggle_sort } from '../shared/use_sortable';

/*
 * PROD-7828 R8 — Additional payments tab. Ops enter money paid TO a teacher for the selected payment
 * month (deductions are R6's penalties; the only negative row is the SYSTEM carry-forward, which can
 * be waived but never edited). Every decision — validation, warnings, duplicate detection, the
 * writable window — lives in params.js / form_logic.js / adapters.js; this file is the I/O shell.
 */
const system_now = () => new Date();

const RESULT_BANNER = {
  success: 'tp-banner-ok',
  warning: 'tp-banner-warn',
  info: 'tp-banner-info'
};

const AdditionalPaymentsTab = ({
  pay_month: shell_month,
  month_label: month_text,
  month_badge,
  now = system_now,
  on_error,
  on_notify,
  on_month_change,
  on_changed
}) => {
  // The shell's months are 'YYYY-MM'; every request below needs the API's 'YYYY-MM-01'.
  const pay_month = api_pay_month(shell_month);
  const [categories, setCategories] = useState([]);
  // Its own state: a list reload clears the tab banner, and must not hide why Add is off.
  const [categories_error, setCategoriesError] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => list_view(null));
  const [state, setState] = useState(() => initial_list_state());
  const [banner, setBanner] = useState('');
  const [result, setResult] = useState(null);
  const [form_open, setFormOpen] = useState(false);
  const [reset_token, setResetToken] = useState(0);
  // CD-113: form_move is the refused previous-month entry the form offers to move to the current month;
  // carry holds it across the month switch that move asks the shell for, form_seed opens the form with it.
  const [form_move, setFormMove] = useState(null);
  const [form_seed, setFormSeed] = useState(null);
  const carry = useRef(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // RM4: saving state and errors per SURFACE, so one request never locks or talks into another's.
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});
  /*
   * IDEMPOTENCY_MISMATCH = the earlier save of that request id went through. already_saved is what the
   * operator lands on (message, saved entry, differing fields); saved_entries remembers every such
   * entry (all months, in sessionStorage) so a re-entry for that teacher-month is questioned at any
   * amount — across a tab switch (which unmounts this tab), a month change and a reload.
   */
  const [already_saved, setAlreadySaved] = useState(null);
  const [saved_entries, setSavedEntries] = useState(() => read_saved_guard());
  useEffect(() => {
    write_saved_guard(saved_entries);
  }, [saved_entries]);
  const table_ref = useRef(null);
  // The two money limits are policy rows; until an endpoint returns them this stays the fallback.
  const [limits, setLimits] = useState(POLICY_FALLBACK);
  // Only the latest list request may render (a slow month-A response under month B).
  const seq = useRef(0);
  // A save or refusal resolves after renders it did not see; its reload must use the month and
  // filters on screen now, never the ones captured when it started (FI-3).
  const month_ref = useRef(pay_month);
  month_ref.current = pay_month;
  const state_ref = useRef(state);
  state_ref.current = state;
  // Answers arrive in closures older than the current render, so "is my surface still open?" is read
  // from refs, never from captured state.
  const form_session = useRef(0);
  const editing_ref = useRef(editing);
  editing_ref.current = editing;
  const removing_ref = useRef(removing);
  removing_ref.current = removing;
  const confirm_ref = useRef(confirm);
  confirm_ref.current = confirm;
  const confirm_queue = useRef([]);
  const confirm_seq = useRef(0);
  // The {uuid, version} an edit went out with and got no clear answer for (RL3): a VERSION_CONFLICT on a
  // resend of that same version is likely its own. A fresh edit carries a newer version (R7H-L6).
  const uncertain_edit = useRef(null);
  // RL7: a dialog whose opener a reload unmounted hands focus to its row's first action (the saved row
  // after a save), else the list heading. focus_uuid is set whenever a dialog opens (R7H-L1).
  const heading_ref = useRef(null);
  const form_ref = useRef(null);
  const focus_uuid = useRef(null);
  const focus_lost = useRef(false);
  const loading_ref = useRef(true);

  const write = useMemo(
    () => month_write_state({ pay_month, month_badge, now: now() }),
    [pay_month, month_badge, now]
  );

  // keep_banner: a reload triggered BY a refusal must not erase the refusal's message.
  const load = useCallback(
    (offset, applied, { keep_banner = false } = {}) => {
      seq.current += 1;
      const mine = seq.current;
      const month = month_ref.current;
      if (!month) {
        setView(list_view(null));
        setLoading(false);
        return;
      }
      setLoading(true);
      fetch_additional_list({ pay_month: month, ...applied }, LIMITS.PAGE_SIZE, offset)
        .then((data) => {
          if (mine !== seq.current) return;
          const next = list_view(data);
          const back = last_page_offset({
            offset,
            row_count: next.rows.length,
            total_count: next.total_count,
            page_size: LIMITS.PAGE_SIZE
          });
          if (back !== null) {
            setState((s) => ({ ...s, offset: back }));
            load(back, applied, { keep_banner });
            return;
          }
          setLimits((l) => policy_limits(data, l));
          setView(next);
          setSavedEntries((list) => refresh_saved_entries(list, data && data.rows));
          if (!keep_banner) setBanner('');
          setLoading(false);
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          const v = error_view(e);
          if (v.is_permission) on_error(e, 'Failed to load the additional payments.');
          setView(list_error_view(e));
          setLoading(false);
        });
    },
    [on_error]
  );

  const reload = (opts) => load(state_ref.current.offset, state_ref.current.applied, opts);

  const load_categories = () => {
    setCategoriesError('');
    fetch_additional_categories()
      .then((data) => {
        setLimits((l) => policy_limits(data, l));
        setCategories(Array.isArray(data.categories) ? data.categories : []);
      })
      .catch((e) => {
        const v = error_view(e);
        if (v.is_permission) on_error(e, 'Failed to load the additional payment categories.');
        else setCategoriesError(categories_error_text(e));
      });
  };

  useEffect(() => {
    load_categories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Async answers are matched to the surface that sent them; a closed or reopened one is not theirs.
  const form_surface = () => {
    const token = form_session.current;
    return { key: SURFACE.FORM, alive: () => form_session.current === token };
  };
  const dialog_surface = (key, ref, value) => ({ key, alive: () => ref.current === value });
  const snapshot = (surface) => ({ key: surface.key, alive: surface.alive() });

  const set_busy = (key, on) => setBusy((b) => ({ ...b, [key]: on }));
  // The move offer belongs to the form error it came with; any new form error replaces both.
  const set_error = (key, text) => {
    setErrors((x) => ({ ...x, [key]: text }));
    if (key === SURFACE.FORM) setFormMove(null);
  };
  const reset_surface = (key) => {
    set_busy(key, false);
    set_error(key, '');
  };

  // A fresh request id per opened form; the session token outlives neither a close nor a month switch.
  const open_form = (seed = null) => {
    form_session.current += 1;
    reset_surface(SURFACE.FORM);
    setFormSeed(seed);
    setResetToken((t) => t + 1);
    setFormOpen(true);
  };
  const close_form = () => {
    form_session.current += 1;
    reset_surface(SURFACE.FORM);
    setFormOpen(false);
  };
  const open_edit = (row) => {
    reset_surface(SURFACE.EDIT);
    focus_uuid.current = row.uuid;
    setEditing(row);
  };
  const close_edit = () => {
    reset_surface(SURFACE.EDIT);
    setEditing(null);
  };
  const open_remove = (row) => {
    reset_surface(SURFACE.REMOVE);
    focus_uuid.current = row.uuid;
    setRemoving(row);
  };
  const close_remove = () => {
    reset_surface(SURFACE.REMOVE);
    setRemoving(null);
  };
  // One confirm shows at a time; another surface's question waits for it (confirm_queue) instead of
  // replacing it. The ref is set eagerly so a second question in the same tick sees the first.
  const close_confirm = () => {
    reset_surface(SURFACE.CONFIRM);
    const next = confirm_queue.current.shift() || null;
    confirm_ref.current = next;
    setConfirm(next);
  };
  const closers = {
    [SURFACE.EDIT]: close_edit,
    [SURFACE.REMOVE]: close_remove,
    [SURFACE.CONFIRM]: close_confirm
  };

  // A confirm sends from itself (its own saving state and error) on behalf of the surface it sits over
  // (owner). A follow-up question from the same owner replaces it.
  const open_confirm = ({ send, ...spec }) => {
    confirm_seq.current += 1;
    const c = { ...spec, id: confirm_seq.current };
    c.run = () => send(dialog_surface(SURFACE.CONFIRM, confirm_ref, c));
    if (confirm_ref.current && confirm_ref.current.owner !== c.owner) {
      confirm_queue.current.push(c);
      return;
    }
    reset_surface(SURFACE.CONFIRM);
    confirm_ref.current = c;
    setConfirm(c);
  };

  useEffect(() => {
    const next = list_state_for_month(state, pay_month);
    if (next !== state) setState(next);
    // Only the CD-113 move keeps an entry across a month switch; any other switch closes the form.
    const moved = carry.current;
    carry.current = null;
    if (moved && moved.month === pay_month) open_form(moved.seed);
    else close_form();
    setResult(null);
    // The saved-entry guard is NOT cleared: it is filtered by the form's pay_month instead.
    setAlreadySaved(null);
    setView(pending_list_view());
    load(next.offset, next.applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay_month]);

  // The saved entry as the list shows it (fresher version/actions) or, under replica lag, as the refusal sent it.
  const highlight_uuid = already_saved && already_saved.entry ? already_saved.entry.uuid : '';
  const listed_saved_row = highlight_uuid ? view.rows.find((r) => r.uuid === highlight_uuid) : null;
  const saved_row = listed_saved_row || (already_saved && already_saved.entry) || null;

  loading_ref.current = loading;
  const restore_focus = () => {
    focus_lost.current = false;
    const uuid = focus_uuid.current;
    const nodes =
      uuid && table_ref.current
        ? [...table_ref.current.querySelectorAll(`[data-row-key="${uuid}"]`)]
        : [];
    const row = nodes.find((n) => n.offsetParent !== null) || nodes[0];
    const target = (row && row.querySelector('.tp-actions button')) || heading_ref.current;
    if (target) target.focus();
  };
  const on_focus_lost = () => {
    focus_lost.current = true;
    if (!loading_ref.current) restore_focus();
  };
  // Every commit: a fast reload can settle in the same render that closed the form (loading never flips).
  useEffect(() => {
    if (!loading && focus_lost.current) restore_focus();
  });

  useEffect(() => {
    if (loading || !listed_saved_row || !table_ref.current) return;
    // Both the table row and the mobile card carry the key; scroll to whichever is rendered.
    const nodes = [...table_ref.current.querySelectorAll(`[data-row-key="${highlight_uuid}"]`)];
    const target = nodes.find((n) => n.offsetParent !== null) || nodes[0];
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [loading, listed_saved_row, highlight_uuid]);

  // R7H-L2: closing the form after a save unmounts its focused Save button (or focus already fell to
  // the body while it was disabled); focus then follows the saved row once the list reloads.
  const close_form_after_save = () => {
    const active = document.activeElement;
    if (
      !active ||
      active === document.body ||
      (form_ref.current && form_ref.current.contains(active))
    ) {
      focus_lost.current = true;
    }
    close_form();
  };

  const set_draft = (key, value) =>
    setState((s) => ({ ...s, drafts: { ...s.drafts, [key]: value } }));

  const apply_filters = () => {
    const next = apply_list_drafts(state);
    setState(next);
    setView(pending_list_view());
    load(0, next.applied);
  };

  const go_page = (offset) => {
    setState((s) => ({ ...s, offset }));
    load(offset, state.applied);
  };

  // Shared by any future Sort control and a table header click; a header click re-fetches (server sort).
  const apply_sort = (next) => {
    setState((s) => ({ ...s, offset: 0, applied: { ...s.applied, sort: next.key, dir: next.dir } }));
    setView(pending_list_view());
    load(0, { ...state.applied, sort: next.key, dir: next.dir });
  };

  const search_teachers = useCallback(
    (q) => fetch_additional_teachers(q).then((d) => (Array.isArray(d.rows) ? d.rows : [])),
    []
  );

  const search_sessions = useCallback(
    (teacher_uuid, q) =>
      teacher_uuid
        ? fetch_additional_sessions({ teacher_uuid, pay_month, q }).then((d) =>
            Array.isArray(d.rows) ? d.rows : []
          )
        : Promise.resolve([]),
    [pay_month]
  );

  // Shared tail of every mutation: surface the recompute outcome (a failed one never hides behind a
  // stale number), keep the server's warnings on screen, and reload the list.
  // started_month: the month the request writes to; its banner is not shown under another.
  const after_mutation = (data, on_done, started_month, origin) => {
    const res = mutation_result(data);
    if (origin.alive()) reset_surface(origin.key);
    focus_uuid.current = res.entry ? res.entry.uuid : null;
    setResult(started_month === month_ref.current ? res : null);
    on_notify(notify_type(res.tone), res.message);
    on_done();
    reload();
    // The month's payable moved: the shell re-reads its lifecycle badge, tab badges and months.
    if (on_changed) on_changed();
  };

  // Opens the Edit modal on a saved entry (a row view), closing whatever led here.
  const edit_saved = (entry) => {
    const row = view.rows.find((r) => r.uuid === entry.uuid) || entry;
    close_confirm();
    close_form();
    open_edit(row);
  };

  /*
   * The earlier save went through. Close every dialog AND the add form (it holds the spent request id,
   * and an empty form invites a second payment); land on the saved entry with an Edit prompt instead.
   */
  const land_on_saved = (saved) => {
    confirm_queue.current = [];
    close_confirm();
    close_edit();
    close_remove();
    close_form_after_save();
    setBanner('');
    setResult(null);
    setAlreadySaved(saved);
    if (saved.entry) {
      focus_uuid.current = saved.entry.uuid;
      const { row } = saved.entry;
      setSavedEntries((list) => remember_saved_entry(list, row));
    }
    reload({ keep_banner: true });
    if (on_changed) on_changed();
  };

  /*
   * retry(extra_confirms, origin) re-runs the same mutation with one more confirmation flag from the
   * dialog that asks for it; it is null for remove, which has no confirmable refusal. sent is the
   * refused create payload (display only). is_save marks a create/update, whose unanswered failure
   * may have committed (FM-4). origin sent the request; parent is the form or Edit modal behind it.
   */
  const handle_failure = (
    e,
    {
      origin,
      parent = origin,
      started_month,
      retry = null,
      sent = null,
      create_form = null,
      is_save = false,
      after_uncertain_edit = false
    }
  ) => {
    if (origin.alive()) set_busy(origin.key, false);
    const raw = is_save ? save_failure_view(error_view(e)) : error_view(e);
    const v = edit_failure_view(raw, after_uncertain_edit);
    if (v.is_permission) {
      on_error(e, 'The change was refused.');
      return;
    }
    if (is_already_saved_error(v)) {
      land_on_saved(already_saved_view(v, sent));
      return;
    }
    const save_month = month_label(started_month);
    const other_month = started_month !== month_ref.current;
    const duplicate_extra = retry && is_duplicate_error(v) ? { confirm_duplicate: true } : null;
    const large_extra = retry && is_confirm_large_error(v) ? { confirm_large: true } : null;
    if (duplicate_extra || large_extra) {
      open_confirm({
        owner: parent.key,
        title: duplicate_extra ? 'Possible duplicate' : 'Confirm a large amount',
        message: other_month ? for_month_text(v.message, save_month) : v.message,
        matches: duplicate_extra ? v.matches : [],
        send: (o) => retry(duplicate_extra || large_extra, o)
      });
      return;
    }
    const route = failure_route(v, snapshot(origin), snapshot(parent));
    route.close.forEach((key) => closers[key]());
    if (route.place === 'banner') {
      // Only a create loses its surface (the form closes on a month switch): name the month it was for.
      setBanner(parent.key === SURFACE.FORM ? lost_create_text(v, save_month) : v.message);
    } else {
      set_error(route.place, other_month ? other_month_failure_text(v, save_month) : v.message);
      if (
        route.place === SURFACE.FORM &&
        v.code === ERROR.PREVIOUS_MONTH_NO_RECORD &&
        create_form
      ) {
        setFormMove(previous_month_carry(create_form, categories));
      }
    }
    // An uncertain save may have committed, and a terminal refusal means the row changed: reload.
    if (v.uncertain || closes_modal(v)) reload({ keep_banner: true });
    // A save that may have committed may have moved the month's payable too.
    if (v.uncertain && on_changed) on_changed();
  };

  const do_create = (form, { add_another, confirms, origin, parent }) => {
    set_busy(origin.key, true);
    set_error(origin.key, '');
    setBanner('');
    const payload = create_payload(form, confirms);
    // R7H-M1: the request's month, not the screen's — a confirm can be answered after a month switch.
    const started_month = api_pay_month(payload.pay_month);
    create_additional(payload)
      .then((data) =>
        after_mutation(
          data,
          () => {
            if (origin.key === SURFACE.CONFIRM && origin.alive()) close_confirm();
            // The form this came from may be gone (a month switch); never reset or close a newer one.
            if (!parent.alive()) return;
            if (add_another) {
              setFormSeed(null);
              setResetToken((t) => t + 1);
            } else close_form_after_save();
          },
          started_month,
          origin
        )
      )
      .catch((e) =>
        handle_failure(e, {
          origin,
          parent,
          started_month,
          retry: (extra, o) =>
            do_create(form, {
              add_another,
              confirms: { ...confirms, ...extra },
              origin: o,
              parent
            }),
          sent: {
            ...payload,
            teacher_name: form.teacher_name,
            category_label: category_label(categories, form.category)
          },
          create_form: form,
          is_save: true
        })
      );
  };

  // The server refuses amount >= confirm_above_inr unless confirm_large is set, so the dialog is
  // what produces the flag — it is never sent without the operator accepting it.
  const start_create = (form, add_another, confirms, origin, parent) => {
    if (!confirms.confirm_large && needs_amount_confirm(form, limits)) {
      open_confirm({
        owner: parent.key,
        title: 'Confirm a large amount',
        message: `This entry is ${fmt(Number(form.amount))}, above the ${fmt(
          limits.confirm_above_inr
        )} review threshold. Save it?`,
        matches: [],
        send: (o) =>
          do_create(form, {
            add_another,
            confirms: { ...confirms, confirm_large: true },
            origin: o,
            parent
          })
      });
      return;
    }
    do_create(form, { add_another, confirms, origin, parent });
  };

  // Q16/Q17: a large amount or a look-alike entry is confirmed, never blocked.
  const check_look_alike = (form, add_another, origin, parent) => {
    const confirms = { confirm_duplicate: false, confirm_large: false };
    const matches = duplicate_matches(form, view.raw_rows);
    if (matches.length) {
      open_confirm({
        owner: parent.key,
        title: 'Possible duplicate',
        message: `${form.teacher_name} already has an entry in ${month_text} with the same category and amount.`,
        matches,
        send: (o) =>
          start_create(form, add_another, { ...confirms, confirm_duplicate: true }, o, parent)
      });
      return;
    }
    start_create(form, add_another, confirms, origin, parent);
  };

  /*
   * After a mismatch, a new entry for that teacher-month is questioned at ANY amount: the look-alike
   * check (same category and amount) misses the likely correction. Editing is the offered action.
   */
  const submit_create = (form, { add_another }) => {
    const surface = form_surface();
    const saved = saved_entry_conflicts(form, saved_entries);
    if (saved.length) {
      open_confirm({
        owner: surface.key,
        title: 'Already saved for this teacher',
        message: `Your earlier save for ${form.teacher_name} in ${month_text} went through (below). If a value in it is wrong, edit that entry — saving this as well pays ${form.teacher_name} both.`,
        matches: saved,
        confirm_label: 'Save as a separate payment',
        primary: { label: 'Edit the saved entry', run: () => edit_saved(row_view(saved[0])) },
        send: (o) => check_look_alike(form, add_another, o, surface)
      });
      return;
    }
    check_look_alike(form, add_another, surface, surface);
  };

  const do_edit = (form, reason, confirms, origin, parent) => {
    set_busy(origin.key, true);
    set_error(origin.key, '');
    setBanner('');
    const started_month = api_pay_month(form.pay_month);
    const unanswered = uncertain_edit.current;
    const after_uncertain_edit =
      !!unanswered && unanswered.uuid === form.uuid && unanswered.version === form.version;
    update_additional(update_payload(form, reason, confirms))
      .then((data) => {
        uncertain_edit.current = null;
        after_mutation(
          data,
          () => {
            if (origin.key === SURFACE.CONFIRM && origin.alive()) close_confirm();
            if (parent.alive()) close_edit();
            // Corrected: the "already saved" prompt has done its job (the re-entry guard stays).
            setAlreadySaved((a) => (a && a.entry && a.entry.uuid === form.uuid ? null : a));
          },
          started_month,
          origin
        );
      })
      .catch((e) => {
        uncertain_edit.current = save_failure_view(error_view(e)).uncertain
          ? { uuid: form.uuid, version: form.version }
          : null;
        handle_failure(e, {
          origin,
          parent,
          started_month,
          retry: (extra, o) => do_edit(form, reason, { ...confirms, ...extra }, o, parent),
          is_save: true,
          after_uncertain_edit
        });
      });
  };

  // An edit hits the same confirm threshold as a create (B6: without this, a large entry could not
  // be corrected either).
  const submit_edit = (form, reason) => {
    const surface = dialog_surface(SURFACE.EDIT, editing_ref, editing);
    const confirms = { confirm_duplicate: false, confirm_large: false };
    if (needs_amount_confirm(form, limits)) {
      open_confirm({
        owner: surface.key,
        title: 'Confirm a large amount',
        message: `This entry is ${fmt(Number(form.amount))}, above the ${fmt(
          limits.confirm_above_inr
        )} review threshold. Save it?`,
        matches: [],
        send: (o) => do_edit(form, reason, { ...confirms, confirm_large: true }, o, surface)
      });
      return;
    }
    do_edit(form, reason, confirms, surface, surface);
  };

  const submit_remove = (row, reason) => {
    const origin = dialog_surface(SURFACE.REMOVE, removing_ref, removing);
    set_busy(origin.key, true);
    set_error(origin.key, '');
    setBanner('');
    const started_month = api_pay_month(row.row.pay_month);
    remove_additional(remove_payload(row.row, reason))
      .then((data) =>
        after_mutation(
          data,
          () => {
            if (origin.alive()) close_remove();
            setSavedEntries((list) => forget_saved_entry(list, row.uuid));
            setAlreadySaved((a) => (a && a.entry && a.entry.uuid === row.uuid ? null : a));
            // Q13 remove + re-add: the wrong-teacher/month entry is gone, so offer a fresh form for the right one.
            if (row.readd_after_remove) open_form();
          },
          started_month,
          origin
        )
      )
      .catch((e) => handle_failure(e, { origin, started_month }));
  };

  const category_filter_options = category_options(categories);
  // aria-disabled, not disabled (CD-114): Add keeps focus as the form opens, Next on the last page.
  const add_off = !write.can_write || form_open || categories.length === 0;
  const prev_off = state.offset <= 0;
  const next_off = state.offset + LIMITS.PAGE_SIZE >= view.total_count;

  // CD-113: the shell switches to the current month; the pay_month effect reopens the form with the entry.
  const current_month = ist_month_key(now());
  const move_to_current = () => {
    carry.current = { month: api_pay_month(current_month), seed: form_move };
    on_month_change(current_month);
  };

  return (
    <div>
      {!write.can_write ? <div className="tp-banner tp-banner-warn">{write.why_not}</div> : null}
      {banner ? (
        <div className="tp-banner tp-banner-error" role="alert">
          {banner}
        </div>
      ) : null}
      {already_saved ? (
        <div className="tp-banner tp-banner-warn" data-testid="tp-already-saved" role="status">
          <div>{already_saved.message}</div>
          {already_saved.differences.length ? (
            <ul className="tp-dup-list">
              {already_saved.differences.map((d) => (
                <li key={d.field}>
                  <b>{d.label}</b>: saved {d.saved_text} · you sent {d.sent_text}
                </li>
              ))}
            </ul>
          ) : null}
          {saved_row ? (
            <div>
              Saved entry: {saved_row.teacher_name} · {saved_row.category_label} ·{' '}
              {saved_row.amount_text} · {saved_row.entry_at_text} · added{' '}
              <span title={saved_row.added_by_title}>{saved_row.added_text}</span>
              {listed_saved_row ? ' — highlighted in the list below.' : ''}
            </div>
          ) : null}
          <div className="tp-modal-ft">
            <button type="button" className="tp-btn-ghost" onClick={() => setAlreadySaved(null)}>
              Dismiss
            </button>
            {/* Q13: a wrong teacher/month cannot be edited — remove it (reason required), then re-add. */}
            {saved_row && already_saved.needs_remove && saved_row.can_remove ? (
              <button
                type="button"
                className="tp-btn"
                onClick={() => open_remove({ ...saved_row, readd_after_remove: true })}
              >
                Remove the saved entry
              </button>
            ) : null}
            {saved_row && !already_saved.needs_remove && saved_row.can_edit ? (
              <button type="button" className="tp-btn" onClick={() => edit_saved(saved_row)}>
                Edit the saved entry
              </button>
            ) : null}
            {saved_row &&
            !(already_saved.needs_remove ? saved_row.can_remove : saved_row.can_edit) &&
            saved_row.why_not ? (
              <span>{saved_row.why_not}</span>
            ) : null}
          </div>
        </div>
      ) : null}
      {result ? (
        <div className={`tp-banner ${RESULT_BANNER[result.tone]}`} role="status">
          {result.message}
          {result.warnings.map((w) => (
            <div key={w.code}>{w.message}</div>
          ))}
        </div>
      ) : null}

      <div className="tp-frow">
        <div style={{ width: '220px' }}>
          <LookupPicker
            id="tp-ap-filter-teacher"
            name="teacher filter"
            label="Filter by teacher"
            label_hidden
            placeholder="Filter by name, slug or email"
            min_chars={LIMITS.TEACHER_QUERY_MIN_LEN}
            value_text={state.drafts.teacher_name}
            search={search_teachers}
            option_key={(t) => t.teacher_uuid}
            render_option={(t) => (
              <span>
                <b>{t.name}</b> · {t.email}
              </span>
            )}
            on_pick={(t) =>
              setState((s) => ({
                ...s,
                drafts: { ...s.drafts, teacher_uuid: t.teacher_uuid, teacher_name: t.name }
              }))
            }
            on_clear={() =>
              setState((s) => ({
                ...s,
                drafts: { ...s.drafts, teacher_uuid: '', teacher_name: '' }
              }))
            }
          />
        </div>
        <select
          className="tp-select"
          value={state.drafts.category}
          onChange={(e) => set_draft('category', e.target.value)}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {category_filter_options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          className="tp-select"
          value={state.drafts.status}
          onChange={(e) => set_draft('status', e.target.value)}
          aria-label="Filter by status"
        >
          <option value={LIST_STATUS.ACTIVE}>Active only</option>
          <option value={LIST_STATUS.REMOVED}>Removed only</option>
          <option value={LIST_STATUS.ALL}>Active and removed</option>
        </select>
        <select
          className="tp-select"
          value={state.drafts.source}
          onChange={(e) => set_draft('source', e.target.value)}
          aria-label="Filter by source"
        >
          <option value="">All sources</option>
          <option value={SOURCE.MANUAL}>Manual</option>
          <option value={SOURCE.CSV}>CSV upload</option>
          <option value={SOURCE.SYSTEM}>System carry-forward</option>
        </select>
        <button type="button" className="tp-btn-out" onClick={apply_filters}>
          Apply
        </button>
        <div className="tp-frow-right">
          {categories_error ? (
            <span className="tp-err" role="alert" data-testid="tp-categories-error">
              {categories_error}{' '}
              <button type="button" className="tp-link" onClick={load_categories}>
                Try again
              </button>
            </span>
          ) : null}
          <button
            type="button"
            className="tp-btn"
            onClick={add_off ? undefined : () => open_form()}
            aria-disabled={add_off ? 'true' : undefined}
            title={write.can_write ? '' : write.why_not}
          >
            + Add entry
          </button>
        </div>
      </div>

      <UploadPanel
        pay_month={pay_month}
        month_text={month_text}
        can_write={write.can_write}
        why_not={write.why_not}
        now={now}
        on_error={on_error}
        on_notify={on_notify}
        on_applied={() => {
          reload({ keep_banner: true });
          if (on_changed) on_changed();
        }}
      />

      {form_open ? (
        <div ref={form_ref}>
          <EntryForm
            pay_month={pay_month}
            month_text={month_text}
            categories={categories}
            limits={limits}
            saving={!!busy[SURFACE.FORM]}
            error_text={errors[SURFACE.FORM] || ''}
            reset_token={reset_token}
            seed={form_seed}
            error_action={
              form_move
                ? { label: `Add to ${month_label(current_month)} instead`, run: move_to_current }
                : null
            }
            now={now}
            new_request_id={uuidv4}
            search_teachers={search_teachers}
            search_sessions={search_sessions}
            on_submit={submit_create}
            on_cancel={close_form}
          />
        </div>
      ) : null}

      <div className="tp-card" ref={table_ref}>
        <div className="tp-card-hd">
          <span className="tp-card-hd-title" ref={heading_ref} tabIndex={-1}>
            {list_header(view, month_text, state.applied.status)}
          </span>
          <span className="tp-card-hd-sub">
            Amounts sit inside gross, before GST and TDS (PRD 22.4)
          </span>
        </div>
        {loading ? <CustomLoading /> : null}
        {!loading && view.list_status === 'error' ? (
          <div className="tp-banner tp-banner-error" role="alert">
            {view.error_text}{' '}
            <button type="button" className="tp-link" onClick={() => reload()}>
              Try again
            </button>
          </div>
        ) : null}
        {!loading && view.list_status !== 'error' ? (
          <EntriesTable
            rows={view.rows}
            highlight_uuid={highlight_uuid}
            on_edit={open_edit}
            on_remove={open_remove}
            sort={{ key: state.applied.sort, dir: state.applied.dir }}
            on_sort={(key) =>
              apply_sort(toggle_sort({ key: state.applied.sort, dir: state.applied.dir }, key))
            }
          />
        ) : null}
      </div>

      {view.total_count > LIMITS.PAGE_SIZE ? (
        <div className="flex items-center gap-3 justify-center mt-3">
          {/* aria-disabled, not disabled: Next on the last page keeps focus (CD-114). */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={
              prev_off ? undefined : () => go_page(Math.max(0, state.offset - LIMITS.PAGE_SIZE))
            }
            aria-disabled={prev_off ? 'true' : undefined}
          >
            Prev
          </button>
          <span className="tp-card-hd-sub">
            {state.offset + 1}–{Math.min(state.offset + view.count, view.total_count)} /{' '}
            {view.total_count}
          </span>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={next_off ? undefined : () => go_page(state.offset + LIMITS.PAGE_SIZE)}
            aria-disabled={next_off ? 'true' : undefined}
          >
            Next
          </button>
        </div>
      ) : null}

      {editing ? (
        <EditModal
          row={editing}
          categories={categories}
          categories_error={categories_error}
          on_retry_categories={load_categories}
          limits={limits}
          saving={!!busy[SURFACE.EDIT]}
          error_text={errors[SURFACE.EDIT] || ''}
          now={now}
          search_sessions={search_sessions}
          on_submit={submit_edit}
          on_close={close_edit}
          on_focus_lost={on_focus_lost}
        />
      ) : null}
      {removing ? (
        <RemoveModal
          row={removing}
          saving={!!busy[SURFACE.REMOVE]}
          error_text={errors[SURFACE.REMOVE] || ''}
          on_submit={submit_remove}
          on_close={close_remove}
          on_focus_lost={on_focus_lost}
        />
      ) : null}
      {confirm ? (
        <ConfirmSaveModal
          key={confirm.id}
          title={confirm.title}
          message={confirm.message}
          matches={confirm.matches}
          saving={!!busy[SURFACE.CONFIRM]}
          error_text={errors[SURFACE.CONFIRM] || ''}
          on_confirm={confirm.run}
          confirm_label={confirm.confirm_label}
          primary={confirm.primary || null}
          on_close={close_confirm}
          on_focus_lost={on_focus_lost}
        />
      ) : null}
    </div>
  );
};

export default AdditionalPaymentsTab;
